/* ---------------------------------------------------------------------------
 * Cart gift tiers
 *
 * Every cart mutation made by the gift system goes through ONE serialized
 * queue (window.__cgtQueue). Nothing here ever runs two cart writes in
 * parallel, because Shopify's /cart/*.js endpoints are not transactional
 * against each other: concurrent writes lose updates and return section HTML
 * rendered from a cart state that no longer exists.
 *
 * All add/remove DECISIONS are made from live /cart.js state, never from the
 * server-rendered data-* attributes. Those attributes are a snapshot from
 * whenever the drawer HTML was last rendered and go stale the moment any other
 * code path (PDP add-to-cart, quick buy, qty stepper) refreshes the drawer.
 * ------------------------------------------------------------------------- */

// Set window.__cgtDebug = true in DevTools to get the verbose logs.
if (!window.__cgtLog) {
  window.__cgtLog = function () {
    if (!window.__cgtDebug) return;
    console.log.apply(console, ['[cart-gift-tiers]'].concat([].slice.call(arguments)));
  };
}

if (!window.__cgtCartRoot) {
  window.__cgtCartRoot = function () {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  };
}

// Authoritative cart state. Always bypasses the HTTP cache.
if (!window.__cgtFetchCart) {
  window.__cgtFetchCart = function () {
    return fetch(`${window.__cgtCartRoot()}cart.js`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    }).then((r) => {
      if (!r.ok) throw new Error(`cart.js ${r.status}`);
      return r.json();
    });
  };
}

if (!window.__cgtIsGiftLine) {
  window.__cgtIsGiftLine = function (item) {
    return !!(item && item.properties && item.properties._is_gift === 'true');
  };
}

/* ---------------------------------------------------------------------------
 * Serialized mutation queue.
 * Every gift-driven cart write chains onto the previous one, so the guard and
 * the tier sync can never interleave with each other or with themselves.
 * ------------------------------------------------------------------------- */
if (!window.__cgtEnqueue) {
  window.__cgtQueue = Promise.resolve();
  window.__cgtEnqueue = function (task) {
    const run = window.__cgtQueue.then(
      () => task(),
      () => task() // a failed predecessor must not poison the queue
    );
    // Keep the chain alive regardless of this task's outcome.
    window.__cgtQueue = run.catch(() => {});
    return run;
  };
}

// Variants that Shopify refused to add (sold out, unavailable, …). Retrying
// them on every cart interaction would spam failed requests forever.
if (!window.__cgtUnaddableVariants) window.__cgtUnaddableVariants = new Set();

// Debug helper — call window.__debugTier1Exclusion() in DevTools.
if (!window.__debugTier1Exclusion) {
  window.__debugTier1Exclusion = function (probeProductId) {
    const cgt = document.querySelector('cart-gift-tiers');
    let listFromDom = null;
    if (cgt) {
      try {
        listFromDom = JSON.parse(cgt.dataset.tier1ExcludedProductIds || 'null');
      } catch (e) {}
    }
    return {
      cgtPresent: !!cgt,
      serverExcludedFlag: cgt ? cgt.dataset.tier1GiftExcluded : '(cgt not present)',
      listFromGlobal: Array.isArray(window.__tier1ExcludedProductIds)
        ? window.__tier1ExcludedProductIds.slice()
        : null,
      listFromDom,
      tiers: cgt
        ? (function () {
            try {
              return JSON.parse(cgt.dataset.tiers).map((t) => ({
                variant_id: t.variant_id,
                threshold: t.threshold,
                excluded: t.excluded,
              }));
            } catch (e) {
              return null;
            }
          })()
        : null,
      unaddableVariants: Array.from(window.__cgtUnaddableVariants),
      probe: probeProductId
        ? { productId: probeProductId, suppressed: window.isTier1GiftAddSuppressed(probeProductId) }
        : '(pass a product ID to test)',
    };
  };
}

// The excluded-product ID list, from the page global with a DOM fallback.
if (!window.__cgtExcludedIds) {
  window.__cgtExcludedIds = function () {
    if (Array.isArray(window.__tier1ExcludedProductIds)) {
      return window.__tier1ExcludedProductIds.map(Number);
    }
    const cgt = document.querySelector('cart-gift-tiers');
    if (cgt) {
      try {
        return (JSON.parse(cgt.dataset.tier1ExcludedProductIds || '[]') || []).map(Number);
      } catch (e) {}
    }
    return [];
  };
}

// Is tier 1's gift suppressed for this cart? Computed from live cart items,
// ignoring gift lines so an auto-added gift can never exclude itself.
if (!window.__cgtTier1ExcludedForCart) {
  window.__cgtTier1ExcludedForCart = function (cart) {
    const ids = window.__cgtExcludedIds();
    if (!ids.length) return false;
    return (cart.items || []).some(
      (item) => !window.__cgtIsGiftLine(item) && ids.indexOf(Number(item.product_id)) !== -1
    );
  };
}

if (!window.isTier1GiftAddSuppressed) {
  window.isTier1GiftAddSuppressed = function (addedProductId) {
    const cgt = document.querySelector('cart-gift-tiers');

    // 1) Server flagged the current cart as containing an excluded product.
    if (cgt && cgt.dataset.tier1GiftExcluded === 'true') {
      window.__cgtLog('tier1 suppressed via server flag');
      return true;
    }

    // 2) The product just added is itself on the exclusion list. The server
    //    flag cannot know about it yet — the drawer HTML predates the add.
    const list = window.__cgtExcludedIds();
    if (!list.length || !addedProductId) return false;

    const match = list.indexOf(Number(addedProductId)) !== -1;
    window.__cgtLog('tier1 exclusion probe', { addedProductId, match });
    return match;
  };
}

/* ---------------------------------------------------------------------------
 * Shared cart-section refresh + checkout guard
 * ------------------------------------------------------------------------- */
if (!window.enforceGiftQtyLimit) {
  window.__cartOpsInFlight = window.__cartOpsInFlight || 0;

  // body class + !important survives GoKwik's MutationObserver, which
  // re-enables checkout buttons after every DOM change.
  if (!document.getElementById('gift-guard-styles')) {
    const s = document.createElement('style');
    s.id = 'gift-guard-styles';
    s.textContent =
      'body.cart-op-in-flight .gokwik-checkout button,' +
      'body.cart-op-in-flight #gokwik-buy-now,' +
      'body.cart-op-in-flight [name="checkout"],' +
      'body.cart-op-in-flight .cart__checkout-button,' +
      'body.cart-op-in-flight .btn--checkout{' +
      'pointer-events:none!important;opacity:0.5!important;cursor:not-allowed!important}';
    document.head.appendChild(s);
  }

  window._disableCheckout = function () {
    window.__cartOpsInFlight++;
    document.body.classList.add('cart-op-in-flight');
  };

  window._enableCheckout = function () {
    window.__cartOpsInFlight = Math.max(0, window.__cartOpsInFlight - 1);
    if (window.__cartOpsInFlight === 0) {
      document.body.classList.remove('cart-op-in-flight');
    }
  };

  // Paints returned section HTML into the drawer. Accepts either a full
  // document string or the sections payload from a cart write.
  window.__cgtPaintSection = function (html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const formHtml = doc.getElementById('AjaxCartForm');
    const subtotalHtml = doc.getElementById('AjaxCartSubtotal');
    const cartEl = document.getElementById('AjaxCartForm');

    if (cartEl && formHtml) {
      cartEl.innerHTML = formHtml.innerHTML;
      if (typeof cartEl.ajaxifyCartItems === 'function') cartEl.ajaxifyCartItems();
    }
    const subtotal = document.getElementById('AjaxCartSubtotal');
    if (subtotal && subtotalHtml) subtotal.innerHTML = subtotalHtml.innerHTML;

    document.querySelectorAll('[data-header-cart-count]').forEach((elm) => {
      const src = cartEl && cartEl.querySelector('[data-cart-count]');
      if (src) elm.textContent = src.textContent;
    });
    document.querySelectorAll('[data-header-cart-total]').forEach((elm) => {
      const src = cartEl && cartEl.querySelector('[data-cart-total]');
      if (src) elm.textContent = src.textContent;
    });
  };

  /* Busy overlay. Ref-counted so nested callers (qty stepper -> tier sync)
     can each show/hide without the overlay flickering in between. */
  window.__cgtBusyCount = window.__cgtBusyCount || 0;

  window.__cgtBusyHost = function () {
    // Anchor to .sidebar__body, not the drawer itself: the drawer also contains
    // the recommendations column (and .sidebar__body is only 65% wide on
    // desktop), so covering the whole drawer put the spinner off-centre.
    // .sidebar__body already carries position:relative in component-sidebar.css.
    const drawer = document.getElementById('site-cart-sidebar');
    if (drawer) return drawer.querySelector('.sidebar__body') || drawer;
    return document.getElementById('AjaxCartForm');
  };

  window.__cgtBusy = function (show) {
    if (show) {
      window.__cgtBusyCount++;
      const host = window.__cgtBusyHost();
      if (!host || host.querySelector('[data-cgt-loader]')) return;
      const loader = document.createElement('div');
      loader.className = 'cart-gift-tiers__loader';
      loader.setAttribute('data-cgt-loader', '');
      loader.setAttribute('role', 'status');
      loader.setAttribute('aria-live', 'polite');
      loader.setAttribute('aria-label', 'Updating cart');
      host.appendChild(loader);
      return;
    }
    window.__cgtBusyCount = Math.max(0, window.__cgtBusyCount - 1);
    if (window.__cgtBusyCount === 0) {
      document.querySelectorAll('[data-cgt-loader]').forEach((el) => el.remove());
    }
  };

  window.__cgtRefreshCartHTML = function () {
    if (!document.getElementById('AjaxCartForm')) return Promise.resolve();
    return fetch('?section_id=helper-cart')
      .then((r) => r.text())
      .then((text) => window.__cgtPaintSection(text))
      .catch((err) => window.__cgtLog('section refresh failed', err));
  };

  // Every gift variant the theme knows about (tier gifts + legacy default gift).
  window.__cgtGiftVariantIds = function () {
    const ids = new Set();
    document.querySelectorAll('cart-gift-tiers').forEach((el) => {
      try {
        JSON.parse(el.getAttribute('data-tiers') || '[]').forEach((t) => {
          if (t.variant_id) ids.add(Number(t.variant_id));
        });
      } catch (e) {}
    });
    const cartItemsEl = document.querySelector('#AjaxCartForm .cart__items');
    const legacyId = cartItemsEl && Number(cartItemsEl.dataset.giftId);
    if (legacyId) ids.add(legacyId);
    return ids;
  };

  /* Gift integrity guard, serialized through the queue:
   *   1. gift line (_is_gift) with qty > 1  -> clamp to 1
   *   2. known gift variant without the property -> remove (manual/console add)
   */
  window.enforceGiftQtyLimit = function () {
    return window.__cgtEnqueue(function () {
      window._disableCheckout();

      return window.__cgtFetchCart()
        .then((cart) => {
          const giftVariantIds = window.__cgtGiftVariantIds();
          const updates = {};

          (cart.items || []).forEach((item) => {
            const isGiftProp = window.__cgtIsGiftLine(item);
            const isGiftVariant = giftVariantIds.has(Number(item.variant_id));

            if (isGiftProp && item.quantity > 1) {
              updates[item.key] = 1;
            } else if (isGiftVariant && !isGiftProp) {
              updates[item.key] = 0;
            }
          });

          if (!Object.keys(updates).length) return null;

          window.__cgtLog('guard correcting cart', updates);
          window.__cgtInternalWrite = true;
          return fetch(`${window.__cgtCartRoot()}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates, sections: 'helper-cart' }),
          })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`update.js ${r.status}`))))
            .then((res) => {
              if (res && res.sections && res.sections['helper-cart']) {
                window.__cgtPaintSection(res.sections['helper-cart']);
              } else {
                return window.__cgtRefreshCartHTML();
              }
            })
            .finally(() => {
              window.__cgtInternalWrite = false;
            });
        })
        .catch((err) => window.__cgtLog('guard failed', err))
        .finally(() => window._enableCheckout());
    });
  };

  // Trigger 1: page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.enforceGiftQtyLimit());
  } else {
    window.enforceGiftQtyLimit();
  }

  // Trigger 2: tab regains focus — catches cross-tab / DevTools manipulation
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') window.enforceGiftQtyLimit();
  });

  // Trigger 3: fetch interceptor — catches /cart/add.js calls the theme did not
  // make (extensions, console scripts, third-party apps). Theme-owned writes
  // set __cartGiftTiersSyncing / __defaultGiftSyncing so they are ignored.
  (function () {
    const _origFetch = window.fetch;
    window.fetch = function (input) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const isExternalAdd =
        /\/cart\/(add|change|update)\.js/.test(url) &&
        !window.__cartGiftTiersSyncing &&
        !window.__defaultGiftSyncing &&
        !window.__cgtInternalWrite;
      const promise = _origFetch.apply(this, arguments);
      if (isExternalAdd) {
        promise
          .then(() => {
            clearTimeout(window.__giftGuardTimer);
            window.__giftGuardTimer = setTimeout(() => {
              // Re-sync tiers too: an external add changes the qualifying total.
              const el = document.querySelector('cart-gift-tiers');
              if (el && typeof el.syncGifts === 'function') el.syncGifts();
              else window.enforceGiftQtyLimit();
            }, 400);
          })
          .catch(() => {});
      }
      return promise;
    };
  })();
}

if (typeof CartGiftTiers !== 'function') {
  const MAX_SYNC_PASSES = 3;
  // At most this many mutating syncs per window before we stand down.
  const SYNC_BUDGET = 6;
  const SYNC_BUDGET_WINDOW_MS = 10000;

  window.__cgtSyncBudget = window.__cgtSyncBudget || { count: 0, since: 0 };

  function claimSyncBudget() {
    const now = Date.now();
    const b = window.__cgtSyncBudget;
    if (now - b.since > SYNC_BUDGET_WINDOW_MS) {
      b.since = now;
      b.count = 0;
    }
    b.count += 1;
    if (b.count > SYNC_BUDGET) {
      console.warn('[cart-gift-tiers] sync budget exhausted; standing down briefly');
      return false;
    }
    return true;
  }

  class CartGiftTiers extends HTMLElement {
    constructor() {
      super();
      // Headless (cart page): data + sync only, no progress bar, no overlay.
      this.headless = this.getAttribute('data-headless') === 'true';
      this.tiers = this._parseJSON('data-tiers', []);
      this.cartTotal = Number(this.getAttribute('data-cart-total')) || 0;
      this.successText = this.getAttribute('data-success-text') || '';
      this.currencyRate =
        window.Shopify && Shopify.currency && Shopify.currency.rate
          ? Number(Shopify.currency.rate)
          : 1;

      if (!this.tiers.length) return;

      // Paint immediately from the server snapshot (no flash), then reconcile
      // the actual gift lines against live cart state.
      this.render();
      this.syncGifts();
    }

    _parseJSON(attr, fallback) {
      try {
        return JSON.parse(this.getAttribute(attr) || JSON.stringify(fallback));
      } catch (e) {
        return fallback;
      }
    }

    _adjusted() {
      const n = this.tiers.length;
      return this.tiers.map((t, i) => ({
        ...t,
        adjustedThreshold: Math.round(t.threshold * this.currencyRate),
        // Centre of column i in an n-column grid: (2i + 1) / (2n) * 100
        markerPos: ((2 * i + 1) / (2 * n)) * 100,
      }));
    }

    render() {
      if (this.headless) return;
      const adjusted = this._adjusted();

      const bar = this.querySelector('[data-cgt-bar]');
      if (bar) bar.style.width = `${this._calcFillPct(adjusted)}%`;

      this.querySelectorAll('[data-cgt-marker]').forEach((marker, i) => {
        const tier = adjusted[i];
        if (!tier) return;
        marker.classList.toggle('is-reached', this.cartTotal >= tier.adjustedThreshold);
      });

      const messageEl = this.querySelector('[data-cgt-message]');
      if (!messageEl) return;

      const nextTier = adjusted.find((t) => this.cartTotal < t.adjustedThreshold);
      if (!nextTier) {
        messageEl.textContent = this.successText;
        messageEl.classList.add('is-complete');
        return;
      }
      const formatted = this._formatMoney(
        nextTier.adjustedThreshold - this.cartTotal,
        (window.KROWN && KROWN.settings && KROWN.settings.shop_money_format) || '${{amount}}'
      );
      messageEl.textContent = (nextTier.message || '').replace('{{ remaining_amount }}', formatted);
      messageEl.classList.remove('is-complete');
    }

    _calcFillPct(adjusted) {
      const T = this.cartTotal;
      const last = adjusted[adjusted.length - 1];

      if (T >= last.adjustedThreshold) return 100;
      if (T <= 0) return 0;

      // Below tier 1: scale from 0% to marker 1's position.
      if (T < adjusted[0].adjustedThreshold) {
        return (T / adjusted[0].adjustedThreshold) * adjusted[0].markerPos;
      }

      // Between tier i and tier i+1: scale between their marker positions.
      for (let i = 0; i < adjusted.length - 1; i++) {
        const lo = adjusted[i];
        const hi = adjusted[i + 1];
        if (T >= lo.adjustedThreshold && T < hi.adjustedThreshold) {
          const ratio = (T - lo.adjustedThreshold) / (hi.adjustedThreshold - lo.adjustedThreshold);
          return lo.markerPos + ratio * (hi.markerPos - lo.markerPos);
        }
      }
      return 100;
    }

    /* The qualifying total deliberately ignores gift lines, so a gift that is
     * not priced at 0 can never push the cart into the next tier (which would
     * cascade) and removing a gift can never drop the cart below its own
     * threshold (which would thrash add/remove forever). */
    _qualifyingTotal(cart) {
      return (cart.items || []).reduce(
        (sum, item) => (window.__cgtIsGiftLine(item) ? sum : sum + item.final_line_price),
        0
      );
    }

    /* Decide the mutations needed to bring `cart` in line with the tiers.
     * Pure function of live cart state — no reliance on rendered attributes. */
    _plan(cart) {
      const updates = {};
      const toAdd = [];
      const total = this._qualifyingTotal(cart);
      // Prefer the live computation. Only fall back to the server-rendered
      // flag when the ID list is unavailable, because that flag describes the
      // cart as it was when the drawer was last rendered — if the customer has
      // since removed the excluded product, trusting it would keep tier 1
      // suppressed forever.
      const haveIdList = window.__cgtExcludedIds().length > 0;
      const tier1Excluded = haveIdList ? window.__cgtTier1ExcludedForCart(cart) : null;
      const giftLines = (cart.items || []).filter(window.__cgtIsGiftLine);

      this.tiers.forEach((tier, index) => {
        // Marker-only tier (no gift product configured) — nothing to sync.
        if (!tier.variant_id) return;

        const variantId = Number(tier.variant_id);
        const adjustedThreshold = Math.round(tier.threshold * this.currencyRate);
        const reached = total >= adjustedThreshold;

        // Every gift line for this variant. There should only ever be one, but
        // a duplicate must be removed rather than silently ignored.
        const existing = giftLines.filter((g) => Number(g.variant_id) === variantId);

        // Tier 1 exclusion: never auto-add, and drop any gift line the customer
        // became ineligible for after adding an excluded product.
        const excluded =
          index === 0 && (tier1Excluded === null ? tier.excluded === true : tier1Excluded);

        if (excluded || !reached) {
          existing.forEach((g) => {
            updates[g.key] = 0;
          });
          return;
        }

        if (!existing.length) {
          if (!window.__cgtUnaddableVariants.has(variantId)) toAdd.push(variantId);
          return;
        }

        // Reached and present: keep exactly one, quantity 1.
        existing.forEach((g, i) => {
          if (i > 0) updates[g.key] = 0;
          else if (g.quantity !== 1) updates[g.key] = 1;
        });
      });

      // Anti-tamper: a known gift variant sitting in the cart WITHOUT the
      // _is_gift property was not put there by the theme (manual add, console,
      // extension). Drop it. If the tier genuinely qualifies, the loop above
      // has already queued a correctly-tagged replacement in toAdd.
      const giftVariantIds = window.__cgtGiftVariantIds();
      (cart.items || []).forEach((item) => {
        if (window.__cgtIsGiftLine(item)) return;
        if (giftVariantIds.has(Number(item.variant_id))) updates[item.key] = 0;
      });

      return { updates, toAdd, total };
    }

    _write(path, payload) {
      window.__cgtInternalWrite = true;
      // Ask for the drawer HTML in the same request. The response is rendered
      // after this write lands, so when the loop converges right afterwards we
      // can paint it directly instead of spending another round trip.
      return fetch(`${window.__cgtCartRoot()}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...payload, sections: 'helper-cart' }),
      })
        .then((r) => r.json().then((body) => ({ ok: r.ok, status: r.status, body })))
        .finally(() => {
          window.__cgtInternalWrite = false;
        });
    }

    /* Reconcile gift lines with the tiers.
     *
     * Runs on the shared queue, sequentially (update THEN add — never both in
     * flight), and re-reads /cart.js between passes so the next decision sees
     * the result of the previous write. Loops until a pass finds nothing to do,
     * which is what makes a tier crossed mid-flight get picked up instead of
     * being lost until the customer happens to touch the cart again. */
    /* Reconcile gift lines with the tiers.
     *
     * seedCart  — a cart object the caller already has (e.g. the /cart/change.js
     *             response). Saves the first round trip and, more importantly,
     *             lets the caller hand us fresher state than a re-read would.
     * opts.forcePaint — repaint the drawer even when nothing changed, so a
     *             caller can delegate its own repaint to us and we end up
     *             painting exactly once.
     *
     * Runs on the shared queue, sequentially (update THEN add — never both in
     * flight), re-reading the cart between passes so the next decision sees the
     * result of the previous write. Loops until a pass finds nothing to do,
     * which is what makes a tier crossed mid-flight get picked up instead of
     * being lost until the customer happens to touch the cart again.
     *
     * Nothing is painted until the cart has settled. Painting mid-sequence is
     * what used to flash a cart still holding a gift it no longer qualified for.
     */
    syncGifts(seedCart, opts) {
      const forcePaint = !!(opts && opts.forcePaint);
      if (!this.tiers.length) {
        return forcePaint ? window.__cgtRefreshCartHTML() : Promise.resolve();
      }

      return window.__cgtEnqueue(() => {
        window.__cartGiftTiersSyncing = true;
        window._disableCheckout();

        let mutated = false;
        let addedSomething = false;
        let converged = false;
        let pendingHTML = null;
        let busy = false;

        const sectionOf = (res) =>
          (res && res.ok && res.body && res.body.sections && res.body.sections['helper-cart']) || null;

        const pass = (n, seed) => {
          if (n >= MAX_SYNC_PASSES) {
            window.__cgtLog('sync hit pass limit; stopping');
            return Promise.resolve();
          }

          const cartPromise = seed ? Promise.resolve(seed) : window.__cgtFetchCart();

          return cartPromise.then((cart) => {
            const { updates, toAdd, total } = this._plan(cart);
            const hasUpdates = Object.keys(updates).length > 0;

            window.__cgtLog(`pass ${n}`, { total, updates, toAdd });

            if (!hasUpdates && !toAdd.length) {
              converged = true;
              return Promise.resolve();
            }

            if (!claimSyncBudget()) return Promise.resolve();

            mutated = true;
            if (!busy) {
              busy = true;
              window.__cgtBusy(true);
            }

            // Seed for the next pass. update.js returns the whole cart, so it
            // can be reused directly; add.js returns only the added lines, so
            // that path has to re-read.
            let nextSeed = null;

            // Removals first: they free the cart up before anything is added,
            // and they must not race the add.
            const step = hasUpdates
              ? this._write('cart/update.js', { updates })
              : Promise.resolve(null);

            return step
              .then((res) => {
                if (res && !res.ok) {
                  window.__cgtLog('update.js rejected', res.status, res.body);
                } else if (res) {
                  nextSeed = res.body && res.body.items ? res.body : null;
                  pendingHTML = sectionOf(res) || pendingHTML;
                }
                if (!toAdd.length) return null;

                return this._write('cart/add.js', {
                  items: toAdd.map((id) => ({
                    id,
                    quantity: 1,
                    properties: { _is_gift: 'true' },
                  })),
                });
              })
              .then((res) => {
                if (res) {
                  if (res.ok) {
                    addedSomething = true;
                    pendingHTML = sectionOf(res) || pendingHTML;
                    nextSeed = null; // add.js returns line items, not the cart
                  } else {
                    // 422 = sold out / unavailable / limit reached. Blacklist so
                    // we stop retrying on every cart interaction, and surface it
                    // instead of failing silently.
                    console.warn(
                      '[cart-gift-tiers] gift add rejected by Shopify',
                      res.status,
                      res.body && (res.body.description || res.body.message)
                    );
                    toAdd.forEach((id) => window.__cgtUnaddableVariants.add(Number(id)));
                  }
                }
                return pass(n + 1, nextSeed);
              });
          });
        };

        return pass(0, seedCart)
          .then(() => {
            if (addedSomething) this._fireConfetti();

            // Only trust the piggy-backed HTML when the loop actually settled;
            // otherwise it may predate a write we never verified.
            if (mutated && converged && pendingHTML) {
              return window.__cgtPaintSection(pendingHTML);
            }
            if (mutated || forcePaint) return window.__cgtRefreshCartHTML();
            return null;
          })
          .catch((err) => console.warn('[cart-gift-tiers] sync failed', err))
          .finally(() => {
            window.__cartGiftTiersSyncing = false;
            if (busy) window.__cgtBusy(false);
            window._enableCheckout();
          });
      });
    }

    _fireConfetti() {
      const canvas = document.getElementById('cart-gift-tiers-confetti');
      if (!canvas || typeof confetti !== 'function') return;
      try {
        canvas.confetti =
          canvas.confetti || confetti.create(canvas, { resize: true, useWorker: true });
        canvas.confetti({ spread: 70, origin: { y: 0.5 } });
      } catch (e) {
        // swallow — confetti is a nice-to-have, never break the cart flow
      }
    }

    _formatMoney(cents, format) {
      const value = (cents / 100).toFixed(2);
      const placeholderMatch = format.match(/\{\{\s*amount[_a-z]*\s*\}\}/);
      if (!placeholderMatch) return format + value;
      const placeholder = placeholderMatch[0];
      if (/no_decimals/.test(placeholder)) {
        return format.replace(placeholder, Math.round(cents / 100).toLocaleString());
      }
      if (/with_comma_separator/.test(placeholder)) {
        const [int, dec] = value.split('.');
        return format.replace(placeholder, `${int.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec}`);
      }
      const [int, dec] = value.split('.');
      return format.replace(placeholder, `${int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${dec}`);
    }
  }

  if (typeof customElements.get('cart-gift-tiers') === 'undefined') {
    customElements.define('cart-gift-tiers', CartGiftTiers);
  }
}
